/**
 * Error Mapper Utility
 * 
 * Maps API errors with the new format to i18n translation keys
 * 
 * IMPORTANT: The system uses CODE + DOMAIN for translation lookup, NOT the message property!
 * 
 * Error Format:
 * {
 *   "code": "CLIENT_NOT_FOUND_OR_INACTIVE",   // Used for translation
 *   "message": "Error occurred",              // IGNORED - not used for display
 *   "domain": "CLIENT",                       // Used for translation (normalized to lowercase)
 *   "statusCode": 400
 * }
 * 
 * Translation key pattern: `error:{domain}.{code}` (domain is normalized to lowercase)
 * Example: "error:client.CLIENT_NOT_FOUND_OR_INACTIVE"
 * 
 * The middleware will:
 * 1. Extract code and domain from the error
 * 2. Normalize domain to lowercase (CLIENT -> client)
 * 3. Build i18n key: error:client.CLIENT_NOT_FOUND_OR_INACTIVE
 * 4. Look up translation in error.json
 * 5. Display translated message (NOT the backend message property)
 */

export interface ApiError {
  code: string;
  message: string;
  domain: string;
  statusCode: number;
  details?: string;
  field?: string;
}

export interface ErrorMappingResult {
  /**
   * The i18n key to use for translation (without namespace prefix)
   * Example: "auth.CLIENT_NOT_FOUND_OR_INACTIVE"
   */
  messageKey: string;
  
  /**
   * Optional field name if error is field-specific
   */
  fieldKey?: string;
  
  /**
   * The original error for fallback purposes
   */
  originalError: ApiError;
}

/**
 * Maps an API error to an i18n translation key
 * @param error - The API error object
 * @returns ErrorMappingResult with the translation key and optional field
 */
export const mapErrorToI18nKey = (error: any): ErrorMappingResult => {
  // Handle RTK Query error structure
  const errorData = error?.data?.error || error?.data || error?.error || error;
  
  // Extract error fields
  const code: string = errorData?.code || 'UNKNOWN_ERROR';
  const domain: string = errorData?.domain || 'general';
  const message: string = errorData?.message || '';
  const statusCode: number = errorData?.statusCode || error?.status || 500;
  const details: string | undefined = errorData?.details;
  const field: string | undefined = errorData?.field;
  
  const apiError: ApiError = {
    code,
    message,
    domain,
    statusCode,
    details,
    field,
  };
  
  // Normalize domain to lowercase for consistency
  const normalizedDomain = domain.toLowerCase();
  
  // Build the i18n key: {domain}.{code}
  const messageKey = `${normalizedDomain}.${code}`;
  
  return {
    messageKey,
    fieldKey: field,
    originalError: apiError,
  };
};

/**
 * Gets a user-friendly error message key with fallbacks
 * @param error - The API error object
 * @returns The i18n key to use, with fallbacks to more general keys
 */
export const getErrorMessageKey = (error: any): string => {
  const mappingResult = mapErrorToI18nKey(error);
  
  // Return the primary key: {domain}.{code}
  // The i18n system will handle fallbacks if the key doesn't exist
  return mappingResult.messageKey;
};

/**
 * Checks if an error is retryable based on status code
 * @param error - The API error object
 * @returns true if the error is retryable
 */
export const isRetryableError = (error: any): boolean => {
  const errorData = error?.data?.error || error?.data || error?.error || error;
  const statusCode = errorData?.statusCode || error?.status || 500;
  
  // Retryable status codes: 502, 503, 504
  const retryableStatuses = [502, 503, 504];
  return retryableStatuses.includes(statusCode);
};

/**
 * Checks if an error requires logout (authentication failure)
 * @param error - The API error object
 * @returns true if user should be logged out
 */
export const isAuthError = (error: any): boolean => {
  const errorData = error?.data?.error || error?.data || error?.error || error;
  const statusCode = errorData?.statusCode || error?.status;
  const code = errorData?.code || '';
  
  // Status 401 or specific auth error codes
  return statusCode === 401 || 
         code === 'UNAUTHORIZED' || 
         code === 'TOKEN_EXPIRED' ||
         code === 'INVALID_TOKEN';
};

/**
 * Domain-specific error code mappings
 * Used for backwards compatibility and special handling
 * 
 * NOTE: The system uses code + domain to build i18n keys, NOT the message property
 * Example: { code: "CLIENT_NOT_FOUND_OR_INACTIVE", domain: "CLIENT" }
 *          Maps to: error:client.CLIENT_NOT_FOUND_OR_INACTIVE
 */
export const DOMAIN_ERROR_CODES = {
  auth: {
    USER_NOT_FOUND: 'auth.USER_NOT_FOUND',
    INVALID_PASSWORD: 'auth.INVALID_PASSWORD',
    CLIENT_NOT_FOUND_OR_INACTIVE: 'auth.CLIENT_NOT_FOUND_OR_INACTIVE',
    CLIENT_NOT_A_STOCK_OWNER: 'auth.CLIENT_NOT_A_STOCK_OWNER',
    INVALID_VERIFICATION_CODE: 'auth.INVALID_VERIFICATION_CODE',
    UNAUTHORIZED: 'auth.UNAUTHORIZED',
    TOKEN_EXPIRED: 'auth.TOKEN_EXPIRED',
    INVALID_TOKEN: 'auth.INVALID_TOKEN',
    COMPANY_NOT_READY: 'auth.COMPANY_NOT_READY',
  },
  client: {
    CLIENT_NOT_FOUND_OR_INACTIVE: 'client.CLIENT_NOT_FOUND_OR_INACTIVE',
    CLIENT_NOT_FOUND: 'client.CLIENT_NOT_FOUND',
    CLIENT_INACTIVE: 'client.CLIENT_INACTIVE',
    CLIENT_NOT_A_STOCK_OWNER: 'client.CLIENT_NOT_A_STOCK_OWNER',
    CLIENT_USER_DEACTIVATED: 'client.CLIENT_USER_DEACTIVATED',
  },
  validation: {
    VALIDATION_ERROR: 'validation.VALIDATION_ERROR',
    REQUIRED_FIELD: 'validation.REQUIRED_FIELD',
    INVALID_FORMAT: 'validation.INVALID_FORMAT',
    INVALID_EMAIL: 'validation.INVALID_EMAIL',
    INVALID_PHONE: 'validation.INVALID_PHONE',
  },
  general: {
    INTERNAL_SERVER_ERROR: 'general.INTERNAL_SERVER_ERROR',
    BAD_REQUEST: 'general.BAD_REQUEST',
    NOT_FOUND: 'general.NOT_FOUND',
    FORBIDDEN: 'general.FORBIDDEN',
    CONFLICT: 'general.CONFLICT',
    UNKNOWN_ERROR: 'general.UNKNOWN_ERROR',
  },
} as const;

/**
 * Get error key from domain-specific mappings
 * @param domain - The error domain
 * @param code - The error code
 * @returns The full i18n key or undefined if not found
 */
export const getDomainErrorKey = (domain: string, code: string): string | undefined => {
  const domainMap = DOMAIN_ERROR_CODES[domain as keyof typeof DOMAIN_ERROR_CODES];
  if (domainMap) {
    return domainMap[code as keyof typeof domainMap];
  }
  return undefined;
};

